import type { FC } from "react";
import React, { useMemo } from "react";

import { getChildrenRecord } from "../../../../../utils/react";
import { UniBoxAdditional } from "../../../../UniBox";
import {
    INDICATOR_COUNTER_SIZE_BY_AVA_PICTURE_SIZE_MAP,
    INDICATOR_SIZE_BY_AVA_PICTURE_SIZE_MAP,
} from "../../../constants/sizes";
import { createDisplayNameForAvaPictureElement } from "../../../utils/getDisplayName";
import { createOffsetClassMap } from "../../../utils/getOffsetClassMap";
import { createTestIdForAvaPictureElement } from "../../../utils/getTestId";

import type { AvaPictureShapes, AvaPictureSizes } from "../../../types";

import classes from "./AvaPictureSlotTopRight.styl";

type AvaPictureSlotTopRightPrivateProps = {
    size: AvaPictureSizes;
    shape: AvaPictureShapes;
    children: React.ReactElement;
};

export type AvaPictureSlotTopRightProps = Omit<
    AvaPictureSlotTopRightPrivateProps,
    "size" | "shape"
>;

const AVA_PICTURE_SLOT_TOP_RIGHT_DISPLAY_NAME =
    createDisplayNameForAvaPictureElement("SlotTopRight");

const AVA_PICTURE_SLOT_TOP_RIGHT_TEST_ID =
    createTestIdForAvaPictureElement("SlotTopRight");

const INDICATOR_OFFSET_CLASS_BY_SIZE_MAPS = {
    circle: createOffsetClassMap("indicator-offsets-circle", classes),
    squircle: createOffsetClassMap("indicator-offsets-squircle", classes),
};

const INDICATOR_COUNTER_OFFSET_CLASS_BY_SIZE_MAPS = {
    circle: createOffsetClassMap("indicator-counter-offsets-circle", classes),
    squircle: createOffsetClassMap(
        "indicator-counter-offsets-squircle",
        classes,
    ),
};

export const AvaPictureSlotTopRight: FC<AvaPictureSlotTopRightProps> = (
    props,
) => {
    const { size, shape, children } =
        props as AvaPictureSlotTopRightPrivateProps;

    const { Indicator, IndicatorCounter } = useMemo(
        () =>
            getChildrenRecord(
                children,
                AVA_PICTURE_SLOT_TOP_RIGHT_DISPLAY_NAME,
                {
                    Indicator: {
                        props: {
                            size: INDICATOR_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                        },
                        optionalRequired: true,
                    },
                    IndicatorCounter: {
                        props: {
                            size: INDICATOR_COUNTER_SIZE_BY_AVA_PICTURE_SIZE_MAP[
                                size
                            ],
                        },
                        optionalRequired: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                },
            ),
        [children, size],
    );

    const offsetClass = (() => {
        if (Indicator) {
            return INDICATOR_OFFSET_CLASS_BY_SIZE_MAPS[shape][size];
        }
        if (IndicatorCounter) {
            return INDICATOR_COUNTER_OFFSET_CLASS_BY_SIZE_MAPS[shape][size];
        }
        return "";
    })();

    const childToRender = Indicator || IndicatorCounter;

    const shouldShowChild = ["16", "20"].indexOf(size) === -1;

    return (
        <UniBoxAdditional.SlotTopRight
            data-tsid={AVA_PICTURE_SLOT_TOP_RIGHT_TEST_ID}
            customVarsStyles={offsetClass}
        >
            {shouldShowChild && childToRender}
        </UniBoxAdditional.SlotTopRight>
    );
};

AvaPictureSlotTopRight.displayName = AVA_PICTURE_SLOT_TOP_RIGHT_DISPLAY_NAME;

export default AvaPictureSlotTopRight;
